// ─── ConfirmationBonsOrtho.jsx ───────────────────────────────────────
// Le pendant Ortho du controle de demande MGD.
//
// MGD renvoie un PDF : on le lit et on compare. Ortho n'a pas de portail,
// ses bons sont remplis a la main, devant la cliente. Il n'existe donc
// AUCUN document a relire, et le seul temoin de ce qui a ete coche est
// Anissa.
//
// D'ou une declaration plutot qu'une lecture. Ce n'est pas une preuve, et
// le texte le dit : on lui demande de confirmer, pas de prouver.
//
// POURQUOI PAS UNE CASE PAR LIGNE
//
// Le guide `BonsACocher` s'affiche pendant qu'elle remplit les bons, papier
// en main, devant la cliente. Y ajouter huit clics obligatoires ajouterait
// de la friction au pire moment. La confirmation vit donc ici, sur la carte
// du plan, une fois les papiers ranges — au meme endroit que le controle
// MGD, pour qu'il n'y ait qu'un seul endroit ou verifier une commande.

import { useMemo, useState } from 'react';
import { repartirSurLesBons } from '../services/clinical/catalog/orthoBonsDeCommande';

const VERT = '#2d5a3d';
const AMBRE = '#8a5a12';
const ENCRE = '#333';
const DOUX = '#777';

const ligneStyle = {
  display: 'flex', alignItems: 'flex-start', gap: 9,
  padding: '6px 0', fontSize: '.82rem',
  borderTop: '1px solid rgba(0,0,0,.07)',
};

export default function ConfirmationBonsOrtho({ planTests = [], catalogue = [], onConfirmer }) {
  const prevus = useMemo(() => (
    (Array.isArray(planTests) ? planTests : [])
      .filter(t => t?.source_lab === 'ortho_analytic')
      .map(t => String(t.code))
  ), [planTests]);

  const catalogueOrtho = useMemo(() => (
    (Array.isArray(catalogue) ? catalogue : [])
      .filter(t => t?.source_lab === 'ortho_analytic')
  ), [catalogue]);

  const [ajustement, setAjustement] = useState(null); // null | Set des codes
  const [envoi, setEnvoi] = useState(false);

  const codesRetenus = ajustement ? [...ajustement] : prevus;
  const { bons } = repartirSurLesBons(codesRetenus);
  const nbBons = bons.length;

  const nomDe = (code) => (
    catalogueOrtho.find(t => t.code === code)?.display_name || code
  );

  const basculer = (code) => {
    setAjustement((prec) => {
      const s = new Set(prec || prevus);
      if (s.has(code)) s.delete(code); else s.add(code);
      return s;
    });
  };

  const confirmer = async () => {
    if (!onConfirmer || codesRetenus.length === 0) return;
    setEnvoi(true);
    try {
      await onConfirmer(codesRetenus, nbBons);
    } finally {
      setEnvoi(false);
    }
  };

  if (prevus.length === 0) return null;

  return (
    <div style={{
      marginTop: 14,
      background: 'rgba(0,0,0,.02)',
      border: '1px solid rgba(0,0,0,.1)',
      borderRadius: 10,
      padding: '12px 16px',
    }}>
      <span style={{ fontWeight: 600, fontSize: '.85rem', color: ENCRE }}>
        Confirmer les bons Ortho
      </span>
      <p style={{ fontSize: '.75rem', color: '#666', margin: '4px 0 10px', lineHeight: 1.5 }}>
        Ortho n’a pas de portail : aucun document ne dit ce que tu as coché.
        C’est toi qui le sais. Confirme, et le montant à refacturer sera calculé
        là-dessus.
      </p>

      {!ajustement && (
        <>
          {prevus.map(code => (
            <div key={code} style={ligneStyle}>
              <span aria-hidden="true" style={{
                flex: '0 0 auto', width: 13, height: 13, marginTop: 3,
                border: `1.5px solid ${VERT}`, borderRadius: 3, background: VERT,
              }} />
              <span style={{ color: ENCRE }}>{nomDe(code)}</span>
            </div>
          ))}
          <p style={{ fontSize: '.75rem', color: DOUX, margin: '8px 0 0' }}>
            {nbBons} bon{nbBons > 1 ? 's' : ''} · {Math.ceil(nbBons * 21.6)}.— de frais de dossier
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            <button
              type="button"
              onClick={confirmer}
              disabled={envoi || !onConfirmer}
              style={{
                fontSize: '.8rem', padding: '6px 14px', borderRadius: 8,
                border: `1px solid ${VERT}`, background: 'rgba(45,90,61,.08)',
                color: VERT, cursor: envoi ? 'wait' : 'pointer',
              }}
            >
              {envoi ? 'Enregistrement…' : 'J’ai coché exactement ça'}
            </button>
            <button
              type="button"
              onClick={() => setAjustement(new Set(prevus))}
              style={{
                fontSize: '.8rem', padding: '6px 14px', borderRadius: 8,
                border: '1px solid rgba(0,0,0,.2)', background: '#fff',
                color: ENCRE, cursor: 'pointer',
              }}
            >
              J’ai coché autre chose
            </button>
          </div>
        </>
      )}

      {ajustement && (
        <>
          <p style={{ fontSize: '.75rem', color: DOUX, margin: '0 0 6px' }}>
            Coche ce que tu as vraiment demandé sur les bons.
          </p>
          {catalogueOrtho.map(t => (
            <label key={t.code} style={{ ...ligneStyle, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={ajustement.has(t.code)}
                onChange={() => basculer(t.code)}
                style={{ marginTop: 2 }}
              />
              <span style={{ color: ajustement.has(t.code) ? ENCRE : DOUX }}>
                {t.display_name}
                <span style={{ color: DOUX, fontVariantNumeric: 'tabular-nums' }}>
                  {' · '}{t.cost_anissa_chf} CHF
                </span>
              </span>
            </label>
          ))}

          <p style={{ fontSize: '.75rem', color: DOUX, margin: '8px 0 0' }}>
            {codesRetenus.length === 0
              ? 'Aucune analyse cochée.'
              : `${nbBons} bon${nbBons > 1 ? 's' : ''} · ${Math.ceil(nbBons * 21.6)}.— de frais de dossier`}
          </p>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            <button
              type="button"
              onClick={confirmer}
              disabled={envoi || codesRetenus.length === 0 || !onConfirmer}
              style={{
                fontSize: '.8rem', padding: '6px 14px', borderRadius: 8,
                border: `1px solid ${AMBRE}`, background: 'rgba(138,90,18,.07)',
                color: AMBRE,
                cursor: codesRetenus.length === 0 ? 'not-allowed' : (envoi ? 'wait' : 'pointer'),
                opacity: codesRetenus.length === 0 ? 0.5 : 1,
              }}
            >
              {envoi ? 'Enregistrement…' : 'Enregistrer la commande réelle'}
            </button>
            <button
              type="button"
              onClick={() => setAjustement(null)}
              style={{
                fontSize: '.8rem', padding: '6px 14px', borderRadius: 8,
                border: '1px solid rgba(0,0,0,.2)', background: '#fff',
                color: ENCRE, cursor: 'pointer',
              }}
            >
              Annuler
            </button>
          </div>
        </>
      )}
    </div>
  );
}
